<template>
    <div class="xeUsageChart heightMAX" :class="{ 'is-overview-compact': isOverviewCompact }" :style="{ overflow: 'hidden' }">
        <drop @dragover="handleBodyDragOver(...arguments)" @drop="handleBodyDrop(...arguments)" class="heightMAX">
            <div class="kbet-custom-reset heightMAX">
                <div class="rounded kbet-widget-energy v2 heightMAX" :style="{ display: 'block', minHeight: isOverviewCompact ? '0' : '100px' }">
                    <div class="title rounded-top">
                        {{ $t(widget.title) }}
                        <div class="right-controls-usage">
                            <div class="safari-style-buttons">
                                <label class="safari-button red">
                                    <input type="radio" value="use" v-model="usageMode" />
                                    <span class="button-dot"></span>
                                </label>
                                <label class="safari-button yellow">
                                    <input type="radio" value="unuse" v-model="usageMode" />
                                    <span class="button-dot"></span>
                                </label>
                            </div>
                            <div v-if="!isSinglePage" class="right-controls">
                                <span class="carousel-control">
                                    <i @click="goToPrev" class="fas fa-angle-left arrow"></i>
                                    <i @click="goToNext" class="fas fa-angle-right arrow"></i>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="energy-usage-area" :style="{
                            minHeight: isOverviewCompact ? '0' : '100px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'stretch',
                            height: isOverviewCompact ? 'calc(100% - 28px)' : 'calc(100% - 40px)',
                        }">
                        <div style="
                                width: 100%;
                                height: 100%;
                                display: flex;
                                flex-direction: column;
                                justify-content: center;
                            ">
                            <usage-chart ref="usageChart" :usageData="usageData" :itemColors="itemColors"
                                :usageMaxValue="usageMaxValue" :unit="displayUnit" :showPrevData="showPrevData"
                                :widget="widget" :cardHeight="cardHeight" :compact="isOverviewCompact"
                                :style="{ width: '100%', height: '100%' }"
                                @update:groupedData="handleGroupedDataUpdate" />
                        </div>
                    </div>
                </div>
            </div>
        </drop>

        <b-modal ref="modalSetting" size="lg" :title="`${$t('설정')} - ${$t('오늘의 에너지 사용량')}`" body-class="p-t-0 p-b-0"
            hide-footer>
            <usage-chart-setting :initSetting="usageSetting" @on-modal-cancel="onModalCancel"
                @on-modal-ok="onModalOK" />
        </b-modal>
    </div>
</template>

<script>
import xeBaseWidget from "@src/views/widget/v1/common/xeBaseWidget.js";
import UsageChart from "@components/vue-chartjs/Charts/UsageChart.vue";
import UsageChartSetting from "@views/widget/v1/setting/xeUsageSetting.vue";
import xelib from "xelib";

const { Unit } = xelib.PointBoxV2;

export default {
    name: "xeUsageChart",
    extends: xeBaseWidget,
    props: ["cardHeight"],
    components: {
        UsageChart,
        UsageChartSetting,
    },
    data() {
        return {
            usageMode: "use", // "use" 또는 "unuse"
            showPrevData: {
                prevDay: true,
                prevYear: true,
            },
            imgBnW: localStorage.contentInverse,
            itemColors: {},
            usageData: [],
            usageMaxValue: 0,
            groupedData: [],
            chartJs: {
                data: {},
                options: {},
            },
            viewMode: "Chart",
            chartLegend: null,
            themeColor: global.xe.parseBool(localStorage.contentInverse)
                ? require("@components/vue-chartjs/Sample/ChartOptions/DashboardChartColor.json").dark
                : require("@components/vue-chartjs/Sample/ChartOptions/DashboardChartColor.json").light,
        };
    },
    computed: {
        isSinglePage() {
            return this.groupedData.length <= 1;
        },
        usageSetting() {
            return JSON.parse(JSON.stringify(this.widget));
        },
        master() {
            return this.$store.getters.master;
        },
        unit() {
            return this.widget?.objects?.main?.tail || "";
        },
        displayUnit() {
            // 기본 displayUnit (에너지별 설정이 없을 때 사용)
            const main = this.widget?.objects?.main;
            return main?.displayUnit || main?.tail || "";
        },
        originalUnit() {
            return this.widget?.objects?.main?.tail || "";
        },
        getSelectedLabels() {
            return this.usageMode === "use" ? "비교" : "현재";
        },
    },
    watch: {
        usageMode(newVal) {
            // 사용 모드에 따라 showPrevData 업데이트
            if (newVal === "use") {
                this.showPrevData.prevDay = true;
                this.showPrevData.prevYear = true;
            } else {
                this.showPrevData.prevDay = false;
                this.showPrevData.prevYear = false;
            }
        },
    },
    created() {
        // widget.objects 초기화
        if (!this.widget.objects) {
            this.widget.objects = {
                main: {
                    label: "",
                    addition: {
                        afterAction: "",
                        afterBind: "Last",
                        fixed: 0,
                    },
                    tail: "",
                    displayUnit: null,
                    point: "",
                    value: 0,
                    self: { type: 0, sTi: "", eTi: "" },
                },
            };
        }
        if (!this.widget.objects.main) {
            this.widget.objects.main = {
                label: "",
                addition: {
                    afterAction: "",
                    afterBind: "Last",
                    fixed: 0,
                },
                tail: "",
                displayUnit: null,
                point: "",
                value: 0,
                self: { type: 0, sTi: "", eTi: "" },
            };
        }
        if (this.widget.objects.main.displayUnit === undefined) {
            this.$set(this.widget.objects.main, "displayUnit", null);
        }
    },
    mounted() {
        // 초기 상태 설정
        this.usageMode = "use";
        // itemColors 초기화
        this.initializeItemColors();
    },
    methods: {
        handleGroupedDataUpdate(newVal) {
            this.groupedData = newVal;
        },
        goToNext() {
            if (this.$refs.usageChart?.nextSlide) {
                this.$refs.usageChart.nextSlide();
            }
        },
        goToPrev() {
            if (this.$refs.usageChart?.prevSlide) {
                this.$refs.usageChart.prevSlide();
            }
        },

        initSetting() {
            this.isPanelExpand = false;
        },
        applyData(data) {
            this.setData(data);
            this.data = data;
        },
        onModalOK(changeSetting) {
            this.$refs.modalSetting.hide();
            this.$emit("on-change-widget", changeSetting);

            this.$nextTick(() => {
                if (this.initSetting) this.initSetting();
                if (this.data) this.setData(this.data, changeSetting);
            });
        },
        getEnergyDisplayUnitSetting(divCd, widget = this.widget) {
            const settings = widget?.objects?.energySettings;
            if (!settings) return null;

            const direct = settings[divCd];
            if (direct && direct.displayUnit != null && direct.displayUnit !== "") {
                return direct.displayUnit;
            }

            const matchedKey = Object.keys(settings).find(
                (key) => key.toLowerCase() === String(divCd).toLowerCase()
            );
            if (!matchedKey) return null;

            const unit = settings[matchedKey].displayUnit;
            return unit != null && unit !== "" ? unit : null;
        },
        setData(data, widgetOverride = null) {
            this.usageData = [];
            const widgetRef = widgetOverride || this.widget;

            data.forEach((d, i) => {
                const divCd = d.divCd;
                const itemOriginalUnit = (d.unit && String(d.unit).trim()) || "";
                const configuredDisplayUnit = this.getEnergyDisplayUnitSetting(divCd, widgetRef);
                const useOriginalUnit = configuredDisplayUnit == null;
                const displayUnit = useOriginalUnit ? itemOriginalUnit : configuredDisplayUnit;
                const shouldConvert = !useOriginalUnit
                    && itemOriginalUnit
                    && displayUnit
                    && displayUnit !== itemOriginalUnit;

                const convertedData = { ...d, unit: itemOriginalUnit };

                if (convertedData.currentConsumpt != null) {
                    convertedData.originalCurrentConsumpt = convertedData.currentConsumpt;
                }
                if (convertedData.prevConsumpt != null) {
                    convertedData.originalPrevConsumpt = convertedData.prevConsumpt;
                }
                if (convertedData.lastConsumpt != null) {
                    convertedData.originalLastConsumpt = convertedData.lastConsumpt;
                }

                if (shouldConvert) {
                    if (convertedData.currentConsumpt != null) {
                        convertedData.currentConsumpt = this.convertUnit(
                            convertedData.currentConsumpt,
                            itemOriginalUnit,
                            displayUnit
                        );
                    }
                    if (convertedData.prevConsumpt != null) {
                        convertedData.prevConsumpt = this.convertUnit(
                            convertedData.prevConsumpt,
                            itemOriginalUnit,
                            displayUnit
                        );
                    }
                    if (convertedData.lastConsumpt != null) {
                        convertedData.lastConsumpt = this.convertUnit(
                            convertedData.lastConsumpt,
                            itemOriginalUnit,
                            displayUnit
                        );
                    }
                }

                convertedData.displayUnit = displayUnit;

                this.usageData.push({
                    ...convertedData,
                    srcPath:
                        i === 0
                            ? "assets/xems/common/widget/usageChart/icon_energy_elec.png"
                            : i === 1
                                ? "assets/xems/common/widget/usageChart/icon_energy_gas.png"
                                : "",
                });
            });

            // 프로그래스바 길이는 원본 값 기준으로 계산
            this.usageMaxValue = this.usageData.reduce((max, obj) => {
                const originalValue = obj.originalCurrentConsumpt != null ? obj.originalCurrentConsumpt : obj.currentConsumpt;
                const number = parseFloat(originalValue);
                if (!isNaN(number)) max = Math.max(max, number);
                return max;
            }, Number.NEGATIVE_INFINITY);
            if (this.usageMaxValue < 100) this.usageMaxValue = 100;
        },
        convertUnit(value, fromUnit, toUnit) {
            // value가 0이거나 null/undefined인 경우 처리
            if (value == null || value === undefined || isNaN(value)) {
                return value;
            }

            // 단위가 없거나 같은 경우 변환 불필요
            if (!fromUnit || !toUnit || fromUnit === toUnit) {
                return value;
            }

            try {
                // 먼저 정방향 변환 시도 (Unit.convert 사용)
                let converted = Unit.convert(value, fromUnit, toUnit);

                // 변환 결과가 원래 값과 같으면 (변환 실패) 역방향 변환 시도
                if (converted === value) {
                    const fromType = Unit.typeIs(fromUnit);
                    const toType = Unit.typeIs(toUnit);

                    // 역방향 변환 규칙 찾기
                    if (
                        fromType &&
                        toType &&
                        Unit.conversion &&
                        Unit.conversion[toType] &&
                        Unit.conversion[toType][fromType]
                    ) {
                        const reverseConv = Unit.conversion[toType][fromType];

                        // Unit.convert의 내부 로직을 따라 역방향 변환 수행
                        // 1. fromUnit을 reverseConv.to로 변환 (같은 타입 내)
                        const step1 = Unit.kilometer(
                            value,
                            Unit.types[fromType][fromUnit],
                            Unit.types[fromType][reverseConv.to]
                        );

                        // 2. 역계수 적용 (1 / base)
                        const step2 = step1 * (1 / reverseConv.base);

                        // 3. reverseConv.unit을 toUnit으로 변환 (같은 타입 내)
                        converted = Unit.kilometer(
                            step2,
                            Unit.types[toType][reverseConv.unit],
                            Unit.types[toType][toUnit]
                        );
                    } else {
                        // 역방향 변환 규칙도 없으면 원래 값 반환
                        console.warn("No conversion rule found (both directions)", {
                            fromUnit,
                            toUnit,
                            fromType,
                            toType,
                        });
                        return value;
                    }
                }

                // 변환 결과가 유효한 숫자인지 확인
                if (converted != null && !isNaN(converted) && isFinite(converted)) {
                    return converted;
                }
                return value;
            } catch (err) {
                console.warn("Unit conversion failed:", err, { value, fromUnit, toUnit });
                return value;
            }
        },
        initializeItemColors() {
            // widget에서 저장된 itemColors 사용, 없으면 기본값 사용
            const savedColors = this.widget?.objects?.itemColors || {};
            const defaultColors = {
                Elec: "#FFD70099",
                Gas: "#FF450077",
                Gt: "#ff493bBB",
                Pv: "#32CD3277",
                Fc: "#00CED177",
            };

            // 저장된 색상이 있으면 사용, 없으면 기본값 사용
            Object.keys(defaultColors).forEach((key) => {
                this.$set(this.itemColors, key, savedColors[key] || defaultColors[key]);
            });
        },
    },
};
</script>

<style scoped>
.prevDay {
    color: #f55667 !important;
    margin-right: 3px !important;
}

.prevYear {
    color: #3dc1fe !important;
    margin-right: 3px !important;
}

.right-controls-usage {
    display: flex;
    align-items: center;
    gap: 10px;
}

.arrow {
    font-size: 16px !important;
}

.arrow:hover {
    color: grey;
    cursor: pointer;
}

.rounded-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.right-controls {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dropdown-checkbox {
    position: relative;
    display: inline-block;
    width: 80px;
}

.dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 6px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    /* line-height: 1; */
    height: 30px;
    transition: all 0.2s ease;
}

.dropdown-trigger:hover {
    border-color: #007bff;
}

.selected-text {
    flex: 1;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
}

.fas.fa-chevron-down {
    margin-left: 8px;
    transition: transform 0.2s ease;
    font-size: 9px;
    color: grey;
}

.fas.fa-chevron-down.rotated {
    transform: rotate(180deg);
}

.dropdown-menu-usage {
    position: absolute;
    top: 100%;
    /* right: 100px; */
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 9999;
    margin-top: 2px;
    display: flex;
    flex-direction: column;
    padding: 0.2rem;
    width: 90px;
    font-weight: 600;
    font-size: 12px;
    box-shadow:
        rgba(50, 50, 93, 0.25) 0px 13px 27px -5px,
        rgba(0, 0, 0, 0.3) 0px 8px 16px -8px;
}

.checkbox-item-usage {
    /* padding: 0px 25px; */
    display: flex;
    align-items: center;
    justify-content: flex-end;
    /* align-items: center;
    justify-content: space-between; */
    height: 20px;
    margin: 3px;
}

/* Dropdown transition animations */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
    transition: all 0.3s ease;
}

.dropdown-fade-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Safari 스타일 라디오 버튼 */
.safari-style-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
}

.safari-button {
    position: relative;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.safari-button:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.safari-button input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
}

.safari-button.red {
    background: linear-gradient(135deg, #ff5f57 0%, #ff4444 100%);
}

.safari-button.yellow {
    background: linear-gradient(135deg, #ffbd2e 0%, #ffb300 100%);
}

.safari-button.green {
    background: linear-gradient(135deg, #28ca42 0%, #4caf50 100%);
}

.safari-button input[type="radio"]:checked+.button-dot {
    opacity: 1;
}

.button-dot {
    width: 4px;
    height: 4px;
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.safari-button input[type="radio"]:checked {
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
}

.safari-button input[type="radio"]:checked+.button-dot {
    opacity: 1;
}

.xeUsageChart.is-overview-compact .carousel-control .arrow {
    font-size: 18px !important;
    padding: 0 4px;
}

.xeUsageChart.is-overview-compact .right-controls-usage {
    gap: 4px;
}
</style>
